/**
 * Shared minimized cards + general avatar for action log tooltip and drawer rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import { findInGameState } from '../actionHistoryManager';
import { getAvatarImage, getOpponentAvatarImage } from '../avatars';
import { API_URL } from '../config/apiConfig';

/** Cache successful GET /order/:id/full?lang=… slices so repeated hovers do not refetch. */
const orderFullDisplaySliceByLangId = new Map();

function cacheKeyForOrderFull(orderId, i18nLanguage) {
  return `${i18nLanguage || 'en-us'}:${orderId}`;
}

export function resolveOrderForDesign(gameState, unitLike) {
  if (!unitLike) return null;
  if (unitLike.instanceId && gameState?.state) {
    const found = findInGameState(gameState, unitLike.instanceId);
    if (found?.order) return found.order;
  }
  return {
    id: unitLike.id,
    name: unitLike.name || 'Unknown',
    faction_name: unitLike.faction_name || unitLike.faction || 'League of Shadows',
    cost: unitLike.cost ?? 0,
    abilities: unitLike.abilities || [],
    attack_rating: unitLike.attack_rating,
    health: unitLike.health,
    description: unitLike.description,
    image: unitLike.image
  };
}

/** One minimized order card; optional skull overlay. zoneName prevents OrderDesign translateY(40px). */
export function TooltipOrderCard({ order, gameState, showSkull, zoneName = 'tooltip' }) {
  const { i18n } = useTranslation();
  const resolvedBase = resolveOrderForDesign(gameState, order);
  const [nameDescriptionOverlay, setNameDescriptionOverlay] = useState(null);

  const orderId = resolvedBase?.id;
  const lang = i18n.language || 'en-us';

  useEffect(() => {
    if (orderId == null) {
      setNameDescriptionOverlay(null);
      return undefined;
    }
    if (order && order.image && typeof order.image === 'string') {
      setNameDescriptionOverlay(null);
      return undefined;
    }
    const key = cacheKeyForOrderFull(orderId, lang);
    const cached = orderFullDisplaySliceByLangId.get(key);
    if (cached) {
      setNameDescriptionOverlay(cached);
      return undefined;
    }
    setNameDescriptionOverlay(null);
    let cancelled = false;
    const url = `${API_URL}/order/${orderId}/full?lang=${encodeURIComponent(lang)}`;
    fetch(url)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`BattleActionLogCards: GET /order/${orderId}/full failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (typeof data?.name !== 'string' || data.name.length === 0) {
          throw new Error('BattleActionLogCards: order full response missing name');
        }
        const slice = { name: data.name };
        if (typeof data.description === 'string') {
          slice.description = data.description;
        }
        orderFullDisplaySliceByLangId.set(key, slice);
        setNameDescriptionOverlay(slice);
      })
      .catch((err) => {
        console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, lang, order]);

  const resolved = useMemo(() => {
    if (!resolvedBase) return null;
    if (!nameDescriptionOverlay) return resolvedBase;
    return { ...resolvedBase, ...nameDescriptionOverlay };
  }, [resolvedBase, nameDescriptionOverlay]);

  if (!resolved) return null;
  return (
    <div className="bal-tooltip-order-wrap">
      <div className="bal-tooltip-order-inner">
        <OrderDesign order={resolved} count={0} minimal zoneName={zoneName} mediaOnly />
      </div>
      {showSkull && (
        <div className="bal-tooltip-kill-overlay" aria-hidden>💀</div>
      )}
    </div>
  );
}

/** General avatar for a player id (same logic as tooltip opponent branch). */
export function TooltipGeneralAvatar({ targetPlayerId, gameState, userId, opponentId }) {
  if (!gameState || targetPlayerId == null) return null;
  const isAutobattlerMode = gameState.isAutobattler || gameState.devAutobattlerMode;
  const isSpectatorMode = gameState.spectatorUserId && gameState.spectatorUserId === userId;
  const shouldUsePlayerAvatars = isAutobattlerMode || isSpectatorMode;

  if (String(targetPlayerId) === String(opponentId)) {
    const opponentAvatars = shouldUsePlayerAvatars
      ? (gameState.playerAvatars?.[targetPlayerId] || {})
      : (gameState.opponentAvatars?.[userId] || {});
    const opponentState = gameState.state?.[targetPlayerId];
    const opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || gameState.opponentGeneral || '';
    const src = getOpponentAvatarImage(opponentGeneral);
    return (
      <div className="bal-tooltip-general-wrap">
        <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
      </div>
    );
  }
  const playerAvatars = gameState.playerAvatars?.[targetPlayerId] || {};
  const playerState = gameState.state?.[targetPlayerId];
  const playerGeneral = playerAvatars.general || playerState?.selectedGeneral || '';
  const src = getAvatarImage(playerGeneral);
  return (
    <div className="bal-tooltip-general-wrap">
      <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
    </div>
  );
}
